import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Col, Row, Label, Input, Table, Button } from "reactstrap";
import axios from "axios";
import Header from "../../../layout/Header/Header";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";

/**
 * Disponible por articulo, talla y color.
 *
 *   Disponible = Stock + Produccion - Por entregar - Facturado
 *
 * Ninguna pieza aparece en dos columnas: "Por entregar" y "Facturado" se
 * reparten el mismo pendiente de surtir, y "Produccion" se reparte entre lo
 * que la orden no ha bajado a corte y lo que ya esta en corte sin recibirse.
 */
function ReporteDisponible() {
  const { user } = useContext(AuthContext);
  const URL_REPORTE_DISPONIBLE = process.env.REACT_APP_URL_REPORTE_DISPONIBLE;
  const URL_AREAS = process.env.REACT_APP_URL_AREAS;

  const [filas, setFilas] = useState([]);
  const [areas, setAreas] = useState([]);
  const [filtroArea, setFiltroArea] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [soloDisponible, setSoloDisponible] = useState(false);

  const cabeceras = () => ({
    headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
  });

  useEffect(() => {
    axios
      .get(URL_AREAS, cabeceras())
      .then((res) => setAreas(res.data.filter((a) => a.tipo !== "Maquilero")))
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    const url = filtroArea
      ? `${URL_REPORTE_DISPONIBLE}?areas=${filtroArea}`
      : URL_REPORTE_DISPONIBLE;
    axios
      .get(url, cabeceras())
      .then((res) => setFilas(res.data))
      .catch((err) => console.log(err));
  }, [filtroArea]);

  const visibles = filas.filter((f) => {
    if (soloDisponible && f.disponible <= 0) return false;
    if (!busqueda) return true;
    const texto = `${f.codigo} ${f.nombre} ${f.color} ${f.talla}`.toLowerCase();
    return texto.includes(busqueda.toLowerCase());
  });

  const totales = visibles.reduce(
    (t, f) => ({
      stock: t.stock + f.stock,
      produccion: t.produccion + f.produccion,
      porEntregar: t.porEntregar + f.porEntregar,
      facturado: t.facturado + f.facturado,
      disponible: t.disponible + f.disponible,
    }),
    { stock: 0, produccion: 0, porEntregar: 0, facturado: 0, disponible: 0 }
  );

  const renglones = () =>
    visibles.map((f) => [
      f.codigo, f.nombre, f.color, f.talla,
      f.stock, f.produccion, f.porEntregar, f.facturado, f.disponible,
    ]);

  const COLUMNAS = ["Codigo", "Articulo", "Color", "Talla", "Stock", "Produccion", "Por Entregar", "Facturado", "Disponible"];

  function PDF() {
    const doc = new jsPDF("landscape");
    const img = new Image();
    img.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img, "png", 120, 8, 45, 22);
    doc.text("Disponible por Talla y Color", 20, 20);
    doc.autoTable({
      head: [COLUMNAS],
      body: renglones(),
      startY: 40,
      foot: [[
        "", "", "", "Totales",
        totales.stock, totales.produccion, totales.porEntregar, totales.facturado, totales.disponible,
      ]],
      showFoot: "lastPage",
    });
    doc.save("Disponible.pdf");
  }

  function excel() {
    const option = {
      fileName: "Disponible",
      datas: [{
        sheetData: visibles.map((f) => ({
          Codigo: f.codigo, Articulo: f.nombre, Color: f.color, Talla: f.talla,
          Stock: f.stock, Produccion: f.produccion, "Por Entregar": f.porEntregar,
          Facturado: f.facturado, Disponible: f.disponible,
        })),
        sheetName: "Disponible",
        sheetFilter: COLUMNAS,
        sheetHeader: COLUMNAS,
      }],
    };
    new ExportJsonExcel(option).saveExcel();
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_inventarios ? (
        <div className="card container col-12">
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <i className="fas fa-calculator" style={{ marginRight: 8, color: "#0ea5e9" }} />
                Disponible
              </h3>
              <p className="page-subtitle">
                Stock + Produccion − Por entregar − Facturado, por talla y color
              </p>
            </div>
            <div>
              <Button color="danger" size="sm" onClick={PDF} style={{ marginRight: 6 }}>
                PDF
              </Button>
              <Button color="success" size="sm" onClick={excel}>
                Excel
              </Button>
            </div>
          </div>

          <Row style={{ marginBottom: 16 }}>
            <Col md={3}>
              <Label>Almacen</Label>
              <Input type="select" value={filtroArea} onChange={(e) => setFiltroArea(e.target.value)}>
                <option value="">Todas las bodegas</option>
                {areas
                  .sort((a, b) => (a.name > b.name ? 1 : -1))
                  .map((a) => (
                    <option value={a._id} key={a._id}>
                      {a.name}
                    </option>
                  ))}
              </Input>
            </Col>
            <Col md={4}>
              <Label>Buscar</Label>
              <Input
                type="text"
                placeholder="Codigo, articulo, color o talla"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </Col>
            <Col md={3} style={{ display: "flex", alignItems: "flex-end" }}>
              <Button
                size="sm"
                color={soloDisponible ? "primary" : "secondary"}
                onClick={() => setSoloDisponible(!soloDisponible)}
              >
                {soloDisponible ? "Viendo solo con disponible" : "Ver solo con disponible"}
              </Button>
            </Col>
          </Row>

          <Table size="sm" striped borderless responsive>
            <thead>
              <tr>
                <th>Codigo</th>
                <th>Articulo</th>
                <th>Color</th>
                <th>Talla</th>
                <th style={{ textAlign: "right" }}>Stock</th>
                <th style={{ textAlign: "right" }}>Produccion</th>
                <th style={{ textAlign: "right" }}>Por Entregar</th>
                <th style={{ textAlign: "right" }}>Facturado</th>
                <th style={{ textAlign: "right" }}>Disponible</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((f) => (
                <tr key={`${f.idArticulo}-${f.idColor}-${f.idTalla}`}>
                  <td>{f.codigo}</td>
                  <td>{f.nombre}</td>
                  <td>{f.color}</td>
                  <td>{f.talla}</td>
                  <td style={{ textAlign: "right" }}>{f.stock}</td>
                  <td style={{ textAlign: "right" }}>{f.produccion}</td>
                  <td style={{ textAlign: "right" }}>{f.porEntregar}</td>
                  <td style={{ textAlign: "right" }}>{f.facturado}</td>
                  <td
                    style={{
                      textAlign: "right",
                      fontWeight: 600,
                      // El disponible negativo significa que se comprometio mas
                      // de lo que hay y de lo que viene en camino.
                      color: f.disponible < 0 ? "#dc2626" : "#0f172a",
                    }}
                  >
                    {f.disponible}
                  </td>
                </tr>
              ))}
            </tbody>
            {visibles.length > 0 ? (
              <tfoot>
                <tr style={{ fontWeight: 600 }}>
                  <td colSpan={4} style={{ textAlign: "right" }}>Totales</td>
                  <td style={{ textAlign: "right" }}>{totales.stock}</td>
                  <td style={{ textAlign: "right" }}>{totales.produccion}</td>
                  <td style={{ textAlign: "right" }}>{totales.porEntregar}</td>
                  <td style={{ textAlign: "right" }}>{totales.facturado}</td>
                  <td style={{ textAlign: "right" }}>{totales.disponible}</td>
                </tr>
              </tfoot>
            ) : undefined}
          </Table>
          {visibles.length === 0 ? (
            <p style={{ textAlign: "center", color: "#94a3b8" }}>Sin renglones con ese filtro</p>
          ) : undefined}
          <br />
        </div>
      ) : undefined}
    </>
  );
}

export default ReporteDisponible;
